/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@reducers/index';
import { getCurrentUser } from '@selectors/user';
import { SurveyPermissions } from '@services/permissions/SurveyPermissions';
import DelegationModal from '@features/delegation-modal';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { BulkButton, countQuestions, ToggledButton } from '@features/survey-question-list/partials/ToggledButton';
import { FloatingToolbar } from '@g17eco/molecules/floating-toolbar';
import {
  BulkActionToolbarProps,
} from '@features/survey-question-list/partials/BulkActionToolbar';
import G17Client, { FlagProperties } from '@services/G17Client';
import { getAnalytics } from '@services/analytics/AnalyticsService';
import { AnalyticsEvents } from '@services/analytics/AnalyticsEvents';
import { QUESTION } from '@constants/terminology';
import { generateErrorToast } from '@g17eco/molecules/toasts';
import { materialityAssessmentApi, Tag } from '@apps/materiality-tracker/api/materiality-assessment';
import { useToggle } from '@hooks/useToggle';

export const AssessmentToolbar = (props: BulkActionToolbarProps) => {
  const { selectedQuestions, handleClose, surveyId, surveyData, initiativeId } = props;
  const dispatch = useAppDispatch();

  const [openDelegationModal, toggleOpenDelegationModal] = useToggle();
  const [isLoading, setLoading] = useState(false);
  const currentUser = useAppSelector(getCurrentUser);

  if (!currentUser) {
    return null;
  }

  const selectedCount = countQuestions(selectedQuestions);
  const canManage = surveyData && SurveyPermissions.canManage(surveyData, currentUser);

  const handleCatch = () => {
    setLoading(false);
    generateErrorToast({
      message: `Could not update ${QUESTION.PLURAL}`,
    });
  };

  const handleBulkFlagChange = (properties: FlagProperties, utrvIds: string[]) => {
    setLoading(true);
    return G17Client.updateSurveyQuestionFlags({ surveyId, utrvIds, properties })
      .then(() => {
        getAnalytics().track(AnalyticsEvents.SurveyBulkAction, {
          utrvIds,
          properties,
          surveyId,
          initiativeId,
        });
        props.triggerBulkAction(properties);
        return handleReload();
      })
      .then(() => {
        setLoading(false);
      })
      .catch(handleCatch);
  };

  const buttons = canManage
    ? [
        <BulkButton
          key={'delegate-button'}
          className='d-flex gap-2'
          tooltip=''
          onClick={toggleOpenDelegationModal}
        >
          <i className='fal fa-chart-simple' />
          <>Delegate</>
        </BulkButton>,
        <ToggledButton
          key={'verification-button'}
          field={'verificationRequired'}
          action={'Verification off'}
          reverseAction={'Verification on'}
          icon={'fal fa-user-pen'}
          reverseIcon={'fas fa-user-pen'}
          handleBulkFlagChange={handleBulkFlagChange}
          selectedQuestions={selectedQuestions}
          className='d-flex gap-2'
        />,
      ]
    : [];

  const handleReload = async () => {
    dispatch(materialityAssessmentApi.util.invalidateTags([{ type: Tag.Survey, id: surveyId }]));
    return props.handleReload?.();
  };

  if (buttons.length === 0) {
    return null;
  }

  return (
    <>
      {isLoading ? <BlockingLoader /> : null}
      <FloatingToolbar handleClose={handleClose} isOpen={selectedCount > 0} items={buttons} />
      <DelegationModal
        isOpen={openDelegationModal}
        survey={surveyData}
        handleSubmit={async () => {
          setLoading(true);
          await handleReload();
          setLoading(false);
        }}
        toggle={toggleOpenDelegationModal}
        initiativeId={initiativeId}
        utrvs={surveyData.fragmentUniversalTrackerValues.filter((q) => selectedQuestions.some((u) => u._id === q._id))}
      />
    </>
  );
};
